import { bulkPatchSenderProxies, deleteSenderProxy, fetchSenderProxies, patchSenderProxy } from '@/api/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import type { SenderProxyListItem } from '@/entities/sender.entity';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { proxyStatusLabel } from './proxy-labels';

const FILTER_ALL = 'all' as const;
const STATUS_OPTIONS = ['available', 'assigned', 'resting', 'cooldown', 'retired'] as const;

export default function ProxyPoolTable({ refreshKey }: { refreshKey: number }) {
	const [items, setItems] = useState<SenderProxyListItem[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [status, setStatus] = useState<string>(FILTER_ALL);
	const [outOfImport, setOutOfImport] = useState<string>(FILTER_ALL);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(true);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchSenderProxies({
				page,
				limit: 25,
				status: status === FILTER_ALL ? undefined : status,
				outOfImport:
					outOfImport === FILTER_ALL ? undefined : outOfImport === 'true',
			});
			setItems(data.items);
			setTotal(data.total);
			setSelected(new Set());
		} finally {
			setLoading(false);
		}
	}, [page, status, outOfImport]);

	useEffect(() => {
		void load();
	}, [load, refreshKey]);

	async function runAction(id: string, action: 'retire' | 'remove' | 'restore') {
		try {
			if (action === 'remove') {
				await deleteSenderProxy(id);
			} else {
				await patchSenderProxy(id, action);
			}
			toast.success('Готово');
			void load();
		} catch {
			// interceptor
		}
	}

	async function runBulk(action: 'retire' | 'remove') {
		if (selected.size === 0) {
			return;
		}
		try {
			const result = await bulkPatchSenderProxies([...selected], action);
			toast.success(`Затронуто: ${result.affected}`);
			void load();
		} catch {
			// interceptor
		}
	}

	function toggleSelected(id: string, checked: boolean) {
		setSelected(prev => {
			const next = new Set(prev);
			if (checked) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	}

	return (
		<div className='flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4'>
			<p className='text-sm font-medium'>Пул прокси</p>

			<div className='flex flex-wrap items-end gap-3'>
				<div className='flex flex-col gap-1.5 min-w-[140px]'>
					<Label>Статус</Label>
					<Select value={status} onValueChange={v => { setPage(1); setStatus(v); }}>
						<SelectTrigger className='w-full'>
							<SelectValue placeholder='Все' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={FILTER_ALL}>Все</SelectItem>
							{STATUS_OPTIONS.map(option => (
								<SelectItem key={option} value={option}>
									{proxyStatusLabel(option)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className='flex flex-col gap-1.5 min-w-[140px]'>
					<Label>Вне импорта</Label>
					<Select value={outOfImport} onValueChange={v => { setPage(1); setOutOfImport(v); }}>
						<SelectTrigger className='w-full'>
							<SelectValue placeholder='Все' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={FILTER_ALL}>Все</SelectItem>
							<SelectItem value='true'>Да</SelectItem>
							<SelectItem value='false'>Нет</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<Button type='button' size='sm' variant='outline' onClick={() => void load()}>
					Обновить
				</Button>
			</div>

			<div className='flex flex-wrap gap-2'>
				<Button type='button' size='sm' variant='outline' disabled={selected.size === 0} onClick={() => void runBulk('retire')}>
					Вывести выбранные
				</Button>
				<Button type='button' size='sm' variant='destructive' disabled={selected.size === 0} onClick={() => void runBulk('remove')}>
					Удалить выбранные
				</Button>
			</div>

			<p className='text-xs text-muted-foreground'>Всего: {total}</p>
			{loading && items.length === 0 ? <p className='text-xs text-muted-foreground'>Загрузка…</p> : null}

			<div className={cn('flex flex-col gap-2', loading && items.length > 0 && 'opacity-60 pointer-events-none')}>
				{items.map(item => (
					<div key={item.id} className='rounded-md border border-border bg-card px-3 py-2 text-xs'>
						<div className='flex items-start gap-2'>
							<input
								type='checkbox'
								className='mt-1'
								checked={selected.has(item.id)}
								onChange={e => toggleSelected(item.id, e.target.checked)}
							/>
							<div className='min-w-0 flex-1'>
								<div className='flex flex-wrap items-center justify-between gap-2'>
									<span className='font-medium break-all'>{item.hostPort}</span>
									<span className='text-muted-foreground'>{proxyStatusLabel(item.status)}</span>
								</div>
								<p className='text-muted-foreground mt-1'>
									запусков {item.lifetimeRunCount}
									{item.outOfImport ? ' · вне импорта' : ''}
									{item.lastFailReason ? ` · ошибка ${item.lastFailReason}` : ''}
								</p>
								<div className='mt-2 flex flex-wrap gap-2'>
									<Button type='button' size='sm' variant='ghost' onClick={() => void runAction(item.id, 'retire')}>
										Вывести
									</Button>
									<Button type='button' size='sm' variant='ghost' onClick={() => void runAction(item.id, 'restore')}>
										Восстановить
									</Button>
									<Button
										type='button'
										size='sm'
										variant='ghost'
										disabled={item.status === 'assigned'}
										title={item.status === 'assigned' ? 'Сначала завершите сессию' : undefined}
										onClick={() => void runAction(item.id, 'remove')}
									>
										Удалить
									</Button>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>

			{total > 25 ? (
				<div className='flex gap-2'>
					<Button type='button' size='sm' variant='outline' disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
						Назад
					</Button>
					<Button
						type='button'
						size='sm'
						variant='outline'
						disabled={page * 25 >= total}
						onClick={() => setPage(p => p + 1)}
					>
						Далее
					</Button>
				</div>
			) : null}
		</div>
	);
}
